/**
 * Per-suburb SEO metadata for /service-areas/$slug pages.
 *
 * Each entry targets one primary local keyword (the head term we want that
 * page to own) plus 2–3 secondary terms woven into the description.
 * Titles are kept under ~60 chars and descriptions under ~158 chars so they
 * render without truncation in Google SERPs.
 */
export type AreaSeo = {
  /** Primary keyword this page targets. */
  primaryKeyword: string;
  /** Supporting keywords woven into the description / keywords meta. */
  secondaryKeywords: string[];
  title: string;
  description: string;
};

export const serviceAreaSeo: Record<string, AreaSeo> = {
  denver: {
    primaryKeyword: "home theater installation Denver",
    secondaryKeywords: ["smart home installation Denver", "AV installer Denver CO", "whole home audio Denver"],
    title: "Home Theater & Smart Home Install Denver CO | Apex AV",
    description:
      "Home theater installation in Denver, CO plus smart home, whole-home audio & lighting control. LoDo to Wash Park. Schedule a free in-home quote.",
  },
  aurora: {
    primaryKeyword: "home theater installation Aurora CO",
    secondaryKeywords: ["smart home Aurora CO", "surround sound installation Aurora", "TV mounting Aurora"],
    title: "Home Theater Installation Aurora CO | Apex AV",
    description:
      "Home theater installation in Aurora, CO with smart lighting, surround sound and TV mounting. Serving Southlands, Saddle Rock and Smoky Hill. Free quote.",
  },
  lakewood: {
    primaryKeyword: "smart home installation Lakewood CO",
    secondaryKeywords: ["home theater Lakewood CO", "in-wall speakers Lakewood", "home automation Lakewood"],
    title: "Smart Home & Home Theater Install Lakewood CO | Apex AV",
    description:
      "Smart home installation in Lakewood, CO — home automation, hidden in-wall speakers and 4K theaters in Belmar, Green Mountain and Bear Creek. Free quote.",
  },
  centennial: {
    primaryKeyword: "audio video installation Centennial CO",
    secondaryKeywords: ["home theater Centennial CO", "smart home Centennial", "multi-room audio Centennial"],
    title: "Audio Video & Home Theater Install Centennial CO | Apex AV",
    description:
      "Audio video installation in Centennial, CO — home theaters, multi-room audio and smart home control for Piney Creek, Walnut Hills and The Highlands homes.",
  },
  "highlands-ranch": {
    primaryKeyword: "home theater installation Highlands Ranch",
    secondaryKeywords: ["basement theater Highlands Ranch", "home automation Highlands Ranch", "Dolby Atmos installer"],
    title: "Home Theater Installation Highlands Ranch CO | Apex AV",
    description:
      "Home theater installation in Highlands Ranch, CO — basement Atmos theaters & whole-home automation in Backcountry and Eastridge. Free design consult.",
  },
  littleton: {
    primaryKeyword: "home automation Littleton CO",
    secondaryKeywords: ["surround sound Littleton", "outdoor TV and speakers Littleton", "home theater Littleton CO"],
    title: "Home Automation & Surround Sound Littleton CO | Apex AV",
    description:
      "Home automation in Littleton, CO with surround sound, outdoor TVs and patio audio. Serving Ken Caryl Ranch, Roxborough Park and downtown Littleton.",
  },
  englewood: {
    primaryKeyword: "AV installation Englewood CO",
    secondaryKeywords: ["home automation Englewood", "commercial AV Englewood", "TV mounting Englewood CO"],
    title: "AV Installation & Home Automation Englewood CO | Apex AV",
    description:
      "AV installation in Englewood, CO for homes and businesses — home automation, TV mounting and audio along Broadway, Old Englewood and Cherry Hills.",
  },
  "cherry-creek": {
    primaryKeyword: "luxury home theater Cherry Creek Denver",
    secondaryKeywords: ["lighting control Cherry Creek", "smart home Cherry Creek", "AV installer Cherry Creek North"],
    title: "Luxury Home Theater & Lighting Cherry Creek | Apex AV",
    description:
      "Luxury home theater & lighting control in Cherry Creek, Denver. Concierge AV design for Cherry Creek North, Hilltop & Polo Club. Free walkthrough.",
  },
  parker: {
    primaryKeyword: "smart home installation Parker CO",
    secondaryKeywords: ["home theater Parker CO", "whole home audio Parker", "outdoor audio Parker CO"],
    title: "Smart Home & Home Theater Install Parker CO | Apex AV",
    description:
      "Smart home installation in Parker, CO — home theaters, whole-home audio and outdoor entertainment in Stonegate, Idyllwilde and The Pinery. Free estimate.",
  },
  "castle-rock": {
    primaryKeyword: "home theater installation Castle Rock CO",
    secondaryKeywords: ["smart home Castle Rock", "structured wiring Castle Rock", "new construction prewire"],
    title: "Home Theater Installation Castle Rock CO | Apex AV",
    description:
      "Home theater installation in Castle Rock, CO plus smart home control and new-construction prewire. Serving The Meadows, Sapphire Pointe and Crystal Valley.",
  },
  "castle-pines": {
    primaryKeyword: "home automation Castle Pines CO",
    secondaryKeywords: ["luxury home theater Castle Pines", "lighting control Castle Pines", "motorized shades Castle Pines"],
    title: "Home Automation & Theater Design Castle Pines CO | Apex AV",
    description:
      "Home automation in Castle Pines, CO — luxury theaters, lighting control and motorized shades for Castle Pines Village estates. Free in-home consultation.",
  },
  franktown: {
    primaryKeyword: "smart home installation Franktown CO",
    secondaryKeywords: ["home theater Franktown CO", "rural wifi and networking Franktown", "outdoor audio Franktown"],
    title: "Smart Home & AV Installation Franktown CO | Apex AV",
    description:
      "Smart home and AV installation in Franktown, CO — home theaters, acreage-wide WiFi, networking and outdoor audio for Douglas County properties.",
  },
  thornton: {
    primaryKeyword: "TV mounting and home theater Thornton CO",
    secondaryKeywords: ["smart home Thornton CO", "surround sound Thornton", "home network install Thornton"],
    title: "TV Mounting & Home Theater Install Thornton CO | Apex AV",
    description:
      "TV mounting, home theater and smart home installation in Thornton, CO. Surround sound, WiFi and automation for North Metro homes. Free on-site quote.",
  },
  westminster: {
    primaryKeyword: "home theater installation Westminster CO",
    secondaryKeywords: ["smart home Westminster CO", "whole home audio Westminster", "home network Westminster"],
    title: "Home Theater & Smart Home Install Westminster CO | Apex AV",
    description:
      "Home theater installation in Westminster, CO with whole-home audio, smart lighting and WiFi upgrades. Serving Legacy Ridge and Westminster Station.",
  },
  arvada: {
    primaryKeyword: "smart home installation Arvada CO",
    secondaryKeywords: ["home theater Arvada CO", "in-ceiling speakers Arvada", "TV mounting Arvada"],
    title: "Smart Home & Home Theater Install Arvada CO | Apex AV",
    description:
      "Smart home installation in Arvada, CO — home theaters, in-ceiling speakers and TV mounting from Olde Town to Candelas and West Woods. Free estimate.",
  },
  broomfield: {
    primaryKeyword: "home automation Broomfield CO",
    secondaryKeywords: ["home theater Broomfield CO", "smart lighting Broomfield", "home networking Broomfield"],
    title: "Home Automation & Theater Install Broomfield CO | Apex AV",
    description:
      "Home automation in Broomfield, CO — theaters, smart lighting, shades and business-grade home networking in Anthem, Broadlands and Interlocken. Free quote.",
  },
  boulder: {
    primaryKeyword: "smart home installation Boulder CO",
    secondaryKeywords: ["home theater Boulder CO", "lighting control Boulder", "AV installer Boulder County"],
    title: "Smart Home & Home Theater Install Boulder CO | Apex AV",
    description:
      "Smart home installation in Boulder, CO — home theaters, lighting control, shading and networking for Mapleton Hill, Chautauqua and foothills homes.",
  },
  golden: {
    primaryKeyword: "home theater installation Golden CO",
    secondaryKeywords: ["smart home Golden CO", "outdoor audio Golden", "AV installer Golden Colorado"],
    title: "Home Theater & Smart Home Install Golden CO | Apex AV",
    description:
      "Home theater installation in Golden, CO plus smart home control and outdoor audio for foothills, Table Mountain and downtown Golden homes. Free quote.",
  },
};
